/**
 * Knowledge-base status pill + toggle button, shared by the document row and
 * grid tile. The toggle marks a file as knowledge base (embeds it for agent
 * search); the pill reflects the async indexing lifecycle.
 */
import { Sparkles } from "lucide-react";
import { cn, StatusPill, type StatusTone } from "@flint/ui";

export type KbStatus = "none" | "pending" | "indexed" | "failed";

const PILL: Record<Exclude<KbStatus, "none">, { label: string; tone: StatusTone }> = {
  pending: { label: "Indexing…", tone: "warn" },
  indexed: { label: "In KB", tone: "sky" },
  failed: { label: "Index failed", tone: "bad" },
};

export function KbStatusPill({ status }: { status: KbStatus }) {
  if (status === "none") return null;
  const p = PILL[status];
  return (
    <StatusPill tone={p.tone} size="xs" dot>
      {p.label}
    </StatusPill>
  );
}

export function KbToggleButton({
  isKb,
  onToggle,
}: {
  isKb: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle(!isKb);
      }}
      title={isKb ? "Remove from knowledge base" : "Add to knowledge base (AI search)"}
      aria-pressed={isKb}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-sm border transition-colors",
        isKb
          ? "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
          : "border-line bg-paper text-muted hover:bg-bg hover:text-ink",
      )}
    >
      <Sparkles size={14} aria-hidden />
    </button>
  );
}
